<template>
  <div ref="containerRef" class="relative" data-test-id="vf-video">
    <base-video
      ref="videoRef"
      v-style="directives?.['v-style']"
      class="full cover"
      v-bind="$props"
      :controls="false"
      :fit
      :muted="autoplay || muted"
      :preload
      @ended="$emit('ended', $event)"
      @pause="$emit('pause', $event)"
      @play="$emit('play', $event)"
      @playing="$emit('playing', $event)"
    />
    <client-only>
      <vf-video-controls
        v-if="showControls"
        class="absolute z-2 c-white duration"
        :class="[brandClasses?.wrapper, classControls]"
        :paused="videoRef?.paused"
        :progress="videoRef?.progress"
        :size="controlsSize || defaultControlsSize"
        :style="styleControls"
        @click="videoRef?.toggle"
      />
    </client-only>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass, Responsive } from '#types/common'
import type { BaseVideo as BaseVideoProps } from '#types/components/base/video'
import type { IconSizes } from '#types/sizes'
import type { StyleValue } from 'vue'

interface VfVideoProps extends BaseVideoProps {
  controls?: boolean
  classControls?: CSSClass
  controlsSize?: IconSizes | Partial<Responsive<IconSizes>>
  styleControls?: StyleValue
  directives?: Record<string, any>
}

interface VfVideoRef extends HTMLVideoElement {
  el: HTMLVideoElement
  paused: boolean
  progress: number
  duration: number
  toggle: () => void
}

const { controls = true, muted = true, classControls } = defineProps<VfVideoProps>()

defineEmits<{
  ended: [e: Event]
  pause: [e: Event]
  play: [e: Event]
  playing: [e: Event]
}>()

const { brandClasses, controlsSize: defaultControlsSize } = useAppConfig().components.vf.video

const videoRef = ref<VfVideoRef>()
const containerRef = ref<HTMLElement>()
const videoDuration = ref(Infinity)
const isInCarousel = inject('vfCarouselElevate', false)
const showControls = computed(() => controls && (isInCarousel || videoDuration.value >= 5))

onMounted(() => {
  const el = containerRef.value?.querySelector('video')
  if (el) {
    const updateDuration = () => {
      videoDuration.value = el.duration || 0
    }
    el.addEventListener('loadedmetadata', updateDuration)
    if (el.duration > 0) updateDuration()
    onUnmounted(() => el.removeEventListener('loadedmetadata', updateDuration))
  }
})

defineExpose({
  videoRef,
  play: () => videoRef.value?.play(),
  pause: () => videoRef.value?.pause(),
  load: () => videoRef.value?.load()
})
</script>
